/**
 * shared/space_economy.ts — CLIENT MIRROR of server/src/lib/space_economy.ts
 * (the server copy is authoritative; keep them byte-identical below the
 * headers). Space Wave 3 — the first supply route: NPC firms, finite belt
 * deposits, shipments down the lane, the ambush/escort odds, the yard's
 * shortage price, patrol readiness. Pure.
 */
import { warH } from './space_stations';
import { oresForTier, sysTierOf } from './space_industry';
import type { SystemSector } from './galaxy';

// ── FIRMS (THE FIRM LAW) ────────────────────────────────────────────────────
export interface FirmDef {
  firmId: bigint; key: string; name: string; civ: string;
  kind: 'miner' | 'yard'; stationKey: string; beltKey: string; hullsTarget: number;
}
/** The firms of the two lanes (THE TWO-LANE LAW): each civ has a miner
 *  hauling to ITS yard. Aurelian: Waystation Aurel (Kessel Run Scar) → Aurel
 *  Prime. Borean: Farlight Terminal (The Long Dark) → Nova Aurel Station.
 *  firmId is the space_firm PK; a miner ships to the yard of its own civ. */
export const FIRM_DEFS: readonly FirmDef[] = [
  { firmId: 1n, key: 'aurel_mining', name: 'Aurel Mining', civ: 'aurelian', kind: 'miner', stationKey: 'sys0:st0', beltKey: 'poi8', hullsTarget: 0 },
  { firmId: 2n, key: 'aurel_yards', name: 'Aurel Prime Yards', civ: 'aurelian', kind: 'yard', stationKey: 'sys0:st2', beltKey: '', hullsTarget: 6 },
  { firmId: 3n, key: 'borean_mining', name: 'Borean Deep Mining', civ: 'borean', kind: 'miner', stationKey: 'sys0:st3', beltKey: 'poi3', hullsTarget: 0 },
  { firmId: 4n, key: 'borean_yards', name: 'Borean Crown Yards', civ: 'borean', kind: 'yard', stationKey: 'sys0:st1', beltKey: '', hullsTarget: 6 },
];
/** The yard a miner ships to: the yard of its own civ (null for a yard). */
export function yardDefFor(civ: string): FirmDef | null {
  return FIRM_DEFS.find((f) => f.civ === civ && f.kind === 'yard') ?? null;
}
/** One-time seed faucet per firm row (PLATFORM → firm, reason firm_seed). */
export const FIRM_SEED_SOV = 60_000n;
/** The bits of a firm's deterministic Identity (the server wraps them in `new Identity(bits)`). */
export function firmIdentityBits(firmId: bigint): bigint { return 0x5f1a_0000n + firmId; }

// ── DEPOSITS (THE DEPOSIT LAW) ──────────────────────────────────────────────
export interface BeltSpec { key: string; name: string; pos: [number, number, number]; radius: number }
/** The Commons Reach's nine belts in space_sector.ts POI order — the index IS
 *  the client's collider-group index, which is what fieldOre hashes on. */
export const SYS0_BELTS: readonly BeltSpec[] = [
  { key: 'poi1', name: 'The Shard Belt', pos: [-320_000, -26_000, 430_000], radius: 7_000 },
  { key: 'poi3', name: 'The Long Dark', pos: [-620_000, 90_000, -360_000], radius: 8_500 },
  { key: 'poi8', name: 'Kessel Run Scar', pos: [300_000, -40_000, -350_000], radius: 15_000 },
  { key: 'poi13', name: 'Homefield Belt', pos: [-14_000, -700, 11_500], radius: 3_200 },
  { key: 'poi16', name: 'The Ashen Reach', pos: [-150_000, 40_000, 1_150_000], radius: 18_000 },
  { key: 'poi17', name: 'Coldline Shoals', pos: [950_000, -180_000, 450_000], radius: 13_000 },
  { key: 'poi18', name: "The Widow's Teeth", pos: [-1_350_000, -60_000, -150_000], radius: 21_000 },
  { key: 'poi19', name: 'Emberfall Drift', pos: [200_000, 260_000, 1_500_000], radius: 16_000 },
  { key: 'poi20', name: 'The Graveyard Ring', pos: [1_400_000, 60_000, 300_000], radius: 24_000 },
];
/** A system's belts: the Commons list, or a derived system's fields as f<i>
 *  (the same order the client builds its collider groups in). */
export function beltsOf(systemId: string, sector: SystemSector | null): BeltSpec[] {
  if (systemId === 'sys0') return [...SYS0_BELTS];
  if (!sector || !/^sys\d{1,3}$/.test(systemId)) return [];
  return sector.fields.map((f, i) => ({ key: `f${i}`, name: `Field ${i + 1}`, pos: [f.pos[0], f.pos[1], f.pos[2]] as [number, number, number], radius: f.radius }));
}
/** The client's fieldOre rule — which ore a belt carries (display; the firm mines its own). */
export function beltOreKey(systemId: string, idx: number): string {
  const pool = oresForTier(sysTierOf(systemId));
  return pool[Math.floor(warH(`${systemId}|field${idx}`) * pool.length) % pool.length].key;
}
export function depositCapacity(radius: number): number { return Math.round(1800 + radius / 10); }
/** Whole hours renew this fraction of the capacity each. */
export const DEPOSIT_RENEW_FRAC_PER_H = 0.06;
/** The beam meters the belt whose centre is within this of the pilot (the
 *  client's collider reach is radius × 1.15..1.2 + 900; a margin on top). */
export function depositReach(radius: number): number { return radius * 1.2 + 1_300; }
/** The renewal since the anchor: whole hours only (the fraction stays banked
 *  by leaving the anchor where it was). */
export function depositRenewed(remaining: number, capacity: number, hoursSince: number): { remaining: number; hours: number } {
  const hours = Math.max(0, Math.floor(hoursSince));
  if (hours === 0) return { remaining, hours: 0 };
  return { remaining: Math.min(capacity, remaining + Math.floor(hours * capacity * DEPOSIT_RENEW_FRAC_PER_H)), hours };
}

// ── THE LANE (THE LANE / AMBUSH / ESCORT / ARRIVAL LAWS) ────────────────────
export const MINER_EXTRACT_U_PER_H = 90;
export const MINER_ORE_KEY = 'drift_ferrite';
export const MINER_FAMILY = 'ferrite_ore';
export const SHIPMENT_LOT_U = 60;
export const SHIPMENT_SPEED_MPS = 320;   // the cosmetic convoys' speed
export const AMBUSH_WINDOW = { start: 0.35, end: 0.7 } as const;
export const AMBUSH_P_UNESCORTED = 0.45;
export const AMBUSH_P_ESCORTED = 0.10;
export const ESCORT_RANGE_M = 4_000;
export const ESCORT_REWARD_SOV = 150n;
export const SHIPMENT_REAP_S = 24 * 3600;
export function shipmentFlightS(from: readonly number[], to: readonly number[]): number {
  return Math.max(60, Math.hypot(to[0] - from[0], to[1] - from[1], to[2] - from[2]) / SHIPMENT_SPEED_MPS);
}
export function shipmentFrac(departMicros: bigint, arriveMicros: bigint, nowMicros: bigint): number {
  const span = Number(arriveMicros - departMicros);
  if (span <= 0) return 1;
  return Math.min(1, Math.max(0, Number(nowMicros - departMicros) / span));
}
export function shipmentPosAt(from: readonly number[], to: readonly number[], departMicros: bigint, arriveMicros: bigint, nowMicros: bigint): [number, number, number] {
  const f = shipmentFrac(departMicros, arriveMicros, nowMicros);
  return [from[0] + (to[0] - from[0]) * f, from[1] + (to[1] - from[1]) * f, from[2] + (to[2] - from[2]) * f];
}
/** The hashed moment inside the ambush window, in micros. */
export function ambushMoment(departMicros: bigint, flightS: number, roll01: number): bigint {
  const at = (AMBUSH_WINDOW.start + roll01 * (AMBUSH_WINDOW.end - AMBUSH_WINDOW.start)) * flightS;
  return departMicros + BigInt(Math.round(at * 1_000_000));
}
export function ambushHit(roll: number, escorted: boolean): boolean {
  return roll < (escorted ? AMBUSH_P_ESCORTED : AMBUSH_P_UNESCORTED);
}

// ── THE YARD (THE YARD LAW) ─────────────────────────────────────────────────
export const HULL_ORE_U = 40;
export const HULLS_MAX = 12;
export const SUPPLY_GOAL_U = 120;
export const SUPPLY_REWARD_MULT = 1.5;
export const SUPPLY_SHORT_HOURS = 2;
export const PRICE_MOD_PER_SHORT_HOUR_PCT = 12;
export const PRICE_MOD_MAX_PCT = 160;
/** The shortage counter is the price cap's own clock: it never runs past the hour the cap is reached, and a
 *  delivery walks it back an hour at once (a yard 55 hours short eased nothing for a day — 2026-09-17). */
export const SHORT_HOURS_CAP = (PRICE_MOD_MAX_PCT - 100) / PRICE_MOD_PER_SHORT_HOUR_PCT;
/** The yard's ore price multiplier from its short hours (hundredths, no float drift). */
export function priceMult(shortHours: number): number {
  return Math.min(PRICE_MOD_MAX_PCT, 100 + PRICE_MOD_PER_SHORT_HOUR_PCT * Math.max(0, Math.floor(shortHours))) / 100;
}
export function applyPriceMod(unit: bigint, mult: number): bigint {
  return mult === 1 ? unit : BigInt(Math.max(1, Math.round(Number(unit) * mult)));
}
/** THE FULL-YARD LAW (space campaign A, 2026-09-30): one hourly slipway step. Below HULLS_MAX with a whole
 *  HULL_ORE_U of ore → consume it, add ONE hull, sell it once (the hull_sale). At the cap → consume nothing, pay
 *  nothing, add nothing (the old step took 40 u and posted 2,000 ⓒ for a hull min() then threw away). Under
 *  40 u → nothing is built. `supplied` (a whole lot is on hand) is what eases the shortage clock either way. */
export interface YardBuild { build: boolean; oreUsed: number; hullsAfter: number; supplied: boolean }
export function yardBuild(hulls: number, ore: number): YardBuild {
  const supplied = ore >= HULL_ORE_U;
  const build = supplied && hulls < HULLS_MAX;
  return { build, oreUsed: build ? HULL_ORE_U : 0, hullsAfter: build ? hulls + 1 : hulls, supplied };
}
/** The yard pays for delivered ore with what it HAS: min(cash, quote) — the lane's arrival and the port's
 *  delivery both. Exposed for a screen (the short-pay risk, report §7.2); the rule itself is unchanged. */
export function yardPayQuote(cash: bigint, quote: bigint): { quote: bigint; pays: bigint; short: bigint } {
  const pays = cash >= quote ? quote : cash > 0n ? cash : 0n;
  return { quote, pays, short: quote - pays };
}

// ── READINESS (THE READINESS LAW) ───────────────────────────────────────────
/** The post each civ's yard staffs: the civ's side of the POI that carries its yard's station
 *  (Aurel Prime for the Aurelian yard, Nova Aurel for the Borean one). */
export interface ReadinessPost { poi: string; side: 0 | 1; yardId: bigint }
export const READINESS_POSTS: Readonly<Record<'aurelian' | 'borean', ReadinessPost>> = {
  aurelian: { poi: 'poi12', side: 0, yardId: 2n },
  borean: { poi: 'poi4', side: 1, yardId: 4n },
};
/** The post a (poi, side) pair staffs, if any yard staffs it. */
export function readinessPostFor(poiKey: string, side: number): ReadinessPost | null {
  for (const p of Object.values(READINESS_POSTS)) if (p.poi === poiKey && p.side === side) return p;
  return null;
}
/** The Aurelian post the first yard's hulls staff: Aurel Prime (kept for the Wave 3 readers). */
export const READINESS_POI = READINESS_POSTS.aurelian.poi;
export function patrolReadiness(hulls: number, target: number): number {
  if (target <= 0) return 1;
  return Math.min(1.5, Math.max(0.35, hulls / target));
}
export function patrolShipsFor(baseShips: number, readiness: number): number {
  return Math.max(1, Math.round(baseShips * readiness));
}

// ── THE RAIDER LAW (real hulls on the lane) ─────────────────────────────────
export const AMBUSH_RAIDERS = 3;
export const AMBUSH_FIGHT_S = 90;
export const AMBUSH_KILL_RANGE_M = 4_000;
export const ESCORT_ATTRITION_S = 30;
export const RAID_KEY_RE = /^raid\d{1,9}:\d$/;
export type AmbushOutcome = 'beaten' | 'took' | 'passed';
/** At the end of the fight: none left → beaten; two or more → they take the run; one → the old odds. */
export function ambushOutcome(raidersAlive: number, escorted: boolean, roll: number): AmbushOutcome {
  if (raidersAlive <= 0) return 'beaten';
  if (raidersAlive >= 2) return 'took';
  return ambushHit(roll, escorted) ? 'took' : 'passed';
}

// ── THE PORT (the planetary port, 2026-09-17) ───────────────────────────────
/** One haul: a freighter hold's worth (the sky lane's CARGO_QTY_MAX). */
export const PORT_LOT_U = 24;
/** Lifetime export cap per port per economy generation (ten hauls). */
export const PORT_LIFETIME_CAP_U = 240;
/** The coin book's working-capital target (THE TWO-BOOKS LAW). */
export const PORT_COIN_TARGET = 60n;
/** The market fund never funds the port below this (= MARKET_FUND_SEED). */
export const PORT_FUND_FLOOR = 400n;
/** The credit wallet's target; the civ's yard tops it up from its surplus. */
export const PORT_CREDIT_TARGET = 2_000n;
/** What a yard keeps before it capitalises its port. */
export const YARD_CAPITAL_RESERVE = 4_000n;
/** A haul offer or ride lapses after six hours. */
export const PORT_HAUL_TTL_S = 6 * 3600;
/** Imported ore enters the shelf this many units per hour. */
export const PORT_RELEASE_U = 12;
/** The ore family the ports trade (1:1 with the Commons `ore` shelf). */
export const PORT_FAMILY = MINER_FAMILY;
/** The port firm ids per civ (the yards are 2 and 4; the miners 1 and 3). */
export function portFirmIdFor(civ: string): bigint { return civ === 'borean' ? 6n : 5n; }
/** Ore the town keeps for its own smelting and smithing: four per ore-consuming slot plus two smith runs. */
export function oreReserve(oreSlots: number): number { return 4 * Math.max(0, oreSlots) + 8; }
/** The hauler's reward: thirty percent of the expected yard sale, twenty credits at least. */
export function haulReward(qty: number, unitSov: bigint): bigint {
  return BigInt(Math.max(20, Math.round(qty * Number(unitSov) * 0.3)));
}
/** THE RELEASE LAW's plan: units the shelf takes this hour and the coins the fund pays — a dry fund buys fewer. */
export function releasePlan(shelf: number, reserve: number, held: number, fundCash: bigint, price: number): { units: number; coins: bigint } {
  const want = Math.max(0, Math.min(PORT_RELEASE_U, held, Math.ceil(2 * reserve - shelf)));
  if (want <= 0) return { units: 0, coins: 0n };
  const unit = BigInt(Math.max(1, Math.round(price)));
  const spendable = fundCash > PORT_FUND_FLOOR ? fundCash - PORT_FUND_FLOOR : 0n;
  const affordable = Number(spendable / unit);
  const units = Math.min(want, affordable);
  return { units, coins: BigInt(units) * unit };
}
/** The port city of a civ: its lowest-id CITY (spaceports serve the cities). */
export function portCityPick<S extends { id: number; tier: string }>(sites: readonly S[], civOf: (s: S) => string | null, civ: string): S | null {
  let best: S | null = null;
  for (const s of sites) {
    if (s.tier !== 'city' || civOf(s) !== civ) continue;
    if (!best || s.id < best.id) best = s;
  }
  return best;
}

// ── THE DEPTH LAW (the exchange is not bottomless) ──────────────────────────
/** Units per (station, commodity, epoch) that move the mark all the way to its floor/ceiling. */
export const DEPTH_UNITS = 1600;
export const DEPTH_SELL_FLOOR = 0.5;
export const DEPTH_BUY_CEIL = 1.6;
/** The mark's multiplier after `units` were already sold (side 'sell') or bought (side 'buy') this epoch. */
export function depthMult(side: 'buy' | 'sell', units: number): number {
  const u = Math.max(0, units);
  if (side === 'sell') return Math.max(DEPTH_SELL_FLOOR, 1 - u / DEPTH_UNITS);
  return Math.min(DEPTH_BUY_CEIL, 1 + u / DEPTH_UNITS);
}

// ── THE PORT RELAY + THE CREW SPLIT + THE DRY CHAIN + THE RECEIPT (space campaign E, 2026-09-30) ──────────────────
/** At most this many named crew share a run's ONE reward with its captain. */
export const PORT_CREW_MAX = 3;
/** A share is basis points of the escrowed reward; the captain keeps what the crew is not named for. */
export const PORT_SPLIT_BPS = 10_000;
/** The terms a run accepted under `port_relay_v1` carries (its receipt names the version). */
export const PORT_TERMS_VERSION = 1;
/** A relay in flight: both hulls within RELAY_RANGE_M, held RELAY_HOLD_S with at most RELAY_MAX_REL_MPS of relative
 *  motion (measured from the two streamed positions — the server keeps no velocities), finished inside the TTL. */
export const RELAY_RANGE_M = 300;
export const RELAY_HOLD_S = 4;
export const RELAY_BEGIN_TTL_S = 60;
export const RELAY_MAX_REL_MPS = 8;
/** A live relay offer (the captain names a pilot at the moment) stands this long. */
export const RELAY_OFFER_S = 300;
/** A receipt is followed through the yard, the store and the war for this long, then closed with that reason. */
export const RECEIPT_FOLLOW_S = 3 * 86_400;

export interface ShareIn { key: string; bps: number }
export interface ShareOut { key: string; sov: bigint }
/** THE ONE-REWARD SPLIT: Hamilton's largest remainder over basis points — each share the floor of its exact part,
 *  the leftover units one each to the largest fractional remainders, ties to the larger share, then to the earlier
 *  row (the captain is row 0). The parts always sum to `total`; 72 at 60/40 is 43/29. No share, no unit. */
export function splitReward(total: bigint, shares: readonly ShareIn[]): ShareOut[] {
  const t = total > 0n ? total : 0n;
  if (!shares.length) return [];
  const bp = shares.map((s) => BigInt(Math.max(0, Math.floor(Number.isFinite(s.bps) ? s.bps : 0))));
  const den = bp.reduce((n, b) => n + b, 0n);
  if (den <= 0n) return shares.map((s, i) => ({ key: s.key, sov: i === 0 ? t : 0n }));
  const rows = shares.map((s, i) => ({ i, key: s.key, bps: bp[i], sov: (t * bp[i]) / den, rem: (t * bp[i]) % den }));
  let left = t - rows.reduce((n, r) => n + r.sov, 0n);
  const order = [...rows].sort((a, b) => (a.rem !== b.rem ? (b.rem > a.rem ? 1 : -1) : a.bps !== b.bps ? (b.bps > a.bps ? 1 : -1) : a.i - b.i));
  for (const r of order) {
    if (left <= 0n) break;
    if (r.bps > 0n) { r.sov += 1n; left -= 1n; }
  }
  return rows.map((r) => ({ key: r.key, sov: r.sov }));
}
/** The shares a run's terms pay: the captain first (the remainder of the crew's), then the crew in the order named. */
export function termsShares(captainKey: string, crew: readonly ShareIn[]): ShareIn[] {
  const crewBps = crew.reduce((n, c) => n + Math.max(0, Math.floor(c.bps)), 0);
  return [{ key: captainKey, bps: Math.max(0, PORT_SPLIT_BPS - crewBps) }, ...crew.map((c) => ({ key: c.key, bps: Math.max(0, Math.floor(c.bps)) }))];
}
/** Why a captain's crew terms cannot stand (null = they can). `partnerKey` '' = no pre-authorised relay. */
export function crewTermsProblem(captainKey: string, crew: readonly ShareIn[], partnerKey: string): string | null {
  if (crew.length > PORT_CREW_MAX) return `Name at most ${PORT_CREW_MAX} crew`;
  const seen = new Set<string>();
  let sum = 0;
  for (const c of crew) {
    if (!c.key) return 'Every crew line names a pilot';
    if (c.key === captainKey) return 'You are the captain — you keep what the crew is not named for';
    if (seen.has(c.key)) return 'Name each crew member once';
    seen.add(c.key);
    if (!Number.isInteger(c.bps) || c.bps < 1 || c.bps > PORT_SPLIT_BPS) return 'Each share is 0.01 % … 100 % of the reward';
    sum += c.bps;
  }
  if (sum > PORT_SPLIT_BPS) return "The crew's shares add up to more than the whole reward";
  if (partnerKey && partnerKey === captainKey) return 'You cannot relay a run to yourself';
  return null;
}

/** What the server knows when a pilot asks to take over a run (the reducer gathers it; the verdict is pure). */
export interface RelayFacts {
  state: string;                 // the haul's state
  relays: number;                // substitutions already made (the custodian revision)
  nowUs: bigint; deadlineUs: bigint;
  authorised: boolean;           // the pre-authorised partner, or a live offer naming the receiver
  receiverIsCustodian: boolean;
  crewSeated: boolean;           // the receiver sits in someone's turret (no hull of their own here)
  receiverMode: string; custodianMode: string; // 'flight' | 'docked' | 'warp' | 'course' | 'ground'
  sameSystem: boolean;
  sameStation: boolean;          // both docked at ONE station
  distM: number;                 // in flight: between the two hulls
  aboardUnits: number; qty: number;
  receiverFree: number;          // the receiver's ACTIVE hull's free hold
}
/** Why a relay cannot happen now (null = it can). The order is the order a player should fix things in. */
export function relayProblem(f: RelayFacts): string | null {
  if (f.state !== 'aboard') return f.state === 'offered' ? 'That run has not been taken yet' : `That run is ${f.state} — a finished run can never be relayed or revived`;
  if (f.nowUs >= f.deadlineUs) return 'The run is past its deadline — it lapses back to the port';
  if (f.relays >= 1) return 'This run has already been relayed once — one substitution per run';
  if (f.receiverIsCustodian) return 'The run is already in your hold';
  if (!f.authorised) return 'The captain has not authorised you to take this run';
  if (f.aboardUnits < f.qty) return "The run's crates are not aboard the custodian's hull";
  if (f.crewSeated) return 'Leave the turret seat — the crates need your own hull';
  const docked = f.receiverMode === 'docked' && f.custodianMode === 'docked';
  const flying = f.receiverMode === 'flight' && f.custodianMode === 'flight';
  if (docked) { if (!f.sameStation) return 'Dock at the same station as the custodian'; }
  else if (flying) {
    if (!f.sameSystem) return 'The custodian is in another system';
    if (!(f.distM <= RELAY_RANGE_M)) return `Close to ${RELAY_RANGE_M} m of the custodian's hull (${Math.round(f.distM)} m now)`;
  } else return 'Both hulls must be in flight in one system, or docked at one station';
  if (f.receiverFree < f.qty) return `Your active hull has ${f.receiverFree} u free — the run needs ${f.qty} u`;
  return null;
}
export interface RelayPair { rx: number; ry: number; rz: number; cx: number; cy: number; cz: number }
/** The relative speed of two hulls over a hold: the change of their separation vector per second. */
export function relativeSpeed(a: RelayPair, b: RelayPair, dtS: number): number {
  if (!(dtS > 0)) return Infinity;
  const dx = (b.rx - b.cx) - (a.rx - a.cx), dy = (b.ry - b.cy) - (a.ry - a.cy), dz = (b.rz - b.cz) - (a.rz - a.cz);
  return Math.hypot(dx, dy, dz) / dtS;
}
/** The hold's own verdict (null = complete it now). */
export function holdProblem(elapsedS: number, relMps: number): string | null {
  if (!(elapsedS >= 0) || elapsedS > RELAY_BEGIN_TTL_S) return 'The transfer timed out — start it again';
  if (elapsedS < RELAY_HOLD_S) return `Hold station beside the hull — ${Math.max(1, Math.ceil(RELAY_HOLD_S - elapsedS))} s more`;
  if (!(relMps <= RELAY_MAX_REL_MPS)) return `Match the custodian's speed (${relMps.toFixed(1)} m/s apart, ≤ ${RELAY_MAX_REL_MPS})`;
  return null;
}

/** THE DRY CHAIN: everything the hourly step looks at before it procures an UP run (economy.md §13.1). */
export interface PortStepIn {
  state: string; pending: boolean;
  yardHulls: number; yardTarget: number; shortHours: number;
  shelf: number; reserve: number;
  exported: number; cap: number;
  book: bigint; unit: bigint;      // the coin book; coins per unit (round(observed price), ≥ 1)
  portCash: bigint; rewardWant: bigint;
}
export type DryCode = 'state' | 'pending' | 'yard_ok' | 'shelf' | 'cap' | 'book' | 'port_cash';
/** Why the port does (or does not) buy a lot this hour — every reason, in a fixed order; `procure` is the decision
 *  the step makes (THE PROCUREMENT LAW). `port_cash` never blocks: a broke port offers a smaller reward. */
export function procurementVerdict(s: PortStepIn): { procure: boolean; reasons: DryCode[] } {
  const reasons: DryCode[] = [];
  if (s.state !== 'active') reasons.push('state');
  if (s.pending) reasons.push('pending');
  if (!(s.shortHours > 0 || s.yardHulls < s.yardTarget)) reasons.push('yard_ok');
  if (!(s.shelf >= PORT_LOT_U + s.reserve)) reasons.push('shelf');
  if (!(s.exported + PORT_LOT_U <= s.cap)) reasons.push('cap');
  if (!(s.book >= s.unit * BigInt(PORT_LOT_U))) reasons.push('book');
  const procure = reasons.length === 0;
  if (s.portCash < s.rewardWant) reasons.push('port_cash');
  return { procure, reasons };
}
/** The book's hourly top-up reaches PORT_COIN_TARGET only: a lot priced above it needs release coins (imports). */
export function topUpReachesLot(unit: bigint): boolean { return unit * BigInt(PORT_LOT_U) <= PORT_COIN_TARGET; }

/** THE RECEIPT CHAIN — what the data proves after a delivery to the yard (mass balance, never segregation: the ore is
 *  fungible, so "the next hull drew on a stock this run fed", never "this lot became that hull"). */
export type ReceiptStage = 'delivered' | 'built' | 'reserved' | 'committed' | 'landed' | 'stopped';
export type ReceiptWait = '' | 'yard_cap' | 'ore_short' | 'next_step' | 'staffs_patrol' | 'store_full' | 'next_feed' | 'no_campaign' | 'followed';
export interface ReceiptState {
  stage: ReceiptStage; wait: ReceiptWait;
  deliveredUs: bigint; builtUs: bigint; reserveUs: bigint;
  reserveHulls: number; hullsSeen: number; campaignId: bigint; committed: number;
}
export interface ReceiptObs {
  nowUs: bigint;
  firstBuildUs: bigint | null;   // the first hull_sale leg at or after the delivery (or the last check, once built)
  buildsSince: number;           // hull_sale legs since the last check
  yardHulls: number; yardTarget: number; yardOre: number;
  storeHulls: number; storeMax: number;
  campaign: { id: bigint; openedUs: bigint; committed: number } | null; // the first campaign opened after the feed
}
/** One step of a receipt's chain: built → reserve fed → committed, or the wait that holds it, or closed after
 *  RECEIPT_FOLLOW_S. `taken` (yard hulls the store took) = hulls seen + hulls built since − hulls now. */
export function advanceReceipt(r: ReceiptState, o: ReceiptObs): ReceiptState {
  if (r.stage === 'committed' || r.stage === 'landed' || r.stage === 'stopped') return r;
  if (o.nowUs - r.deliveredUs > BigInt(RECEIPT_FOLLOW_S) * 1_000_000n) return { ...r, stage: 'stopped', wait: 'followed' };
  let s = { ...r };
  if (s.stage === 'delivered') {
    if (o.firstBuildUs === null) {
      return { ...s, hullsSeen: o.yardHulls, wait: o.yardHulls >= HULLS_MAX ? 'yard_cap' : o.yardOre < HULL_ORE_U ? 'ore_short' : 'next_step' };
    }
    // Built — and the store may already have taken a hull in the same hour: judged below from the hulls seen at the
    // LAST check (a build and a feed can land between two checks).
    s = { ...s, stage: 'built', wait: '', builtUs: o.firstBuildUs };
  }
  if (s.stage === 'built') {
    const taken = s.hullsSeen + Math.max(0, o.buildsSince) - o.yardHulls;
    if (taken > 0) return { ...s, stage: 'reserved', wait: o.campaign ? '' : 'no_campaign', reserveUs: o.nowUs, reserveHulls: taken, hullsSeen: o.yardHulls };
    return { ...s, hullsSeen: o.yardHulls, wait: o.yardHulls <= o.yardTarget ? 'staffs_patrol' : o.storeHulls >= o.storeMax ? 'store_full' : 'next_feed' };
  }
  if (s.stage === 'reserved') {
    if (o.campaign && o.campaign.openedUs >= s.reserveUs) return { ...s, stage: 'committed', wait: '', campaignId: o.campaign.id, committed: o.campaign.committed };
    return { ...s, wait: 'no_campaign' };
  }
  return s;
}
